import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { contestApi, contestError, type ContestSettings as SettingsState } from './contestApi';
import './DomainContests.css';

export function ContestSettings({ showToast }: {
  showToast: (type: 'success' | 'error', message: string) => void;
}): JSX.Element {
  const [config, setConfig] = useState<SettingsState | null>(null);
  const [emails, setEmails] = useState('');
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await contestApi.settings();
      setConfig(next);
      setEmails(next.contact_emails.join('\n'));
      setError(null);
    } catch (err) {
      setError(contestError(err));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const next = await contestApi.saveSettings({
        contact_emails: emails.split(/\r?\n/).map((email) => email.trim()).filter(Boolean),
        ...(key.trim() ? { captcha_api_key: key.trim() } : {}),
      });
      setConfig(next);
      setEmails(next.contact_emails.join('\n'));
      setKey('');
      showToast('success', 'Domain contest settings saved.');
    } catch (err) {
      setError(contestError(err));
    } finally {
      setSaving(false);
    }
  }

  const dirty = config !== null && (emails !== config.contact_emails.join('\n') || key.trim() !== '');
  return (
    <section className="set-card">
      <div className="set-card-header">
        <div>
          <h2 className="set-card-title">Domain Contests</h2>
          <p className="set-card-subtitle">Contact emails and CAPTCHA service used by contest runs. Only admins can change these settings.</p>
        </div>
      </div>
      <form className="set-team-pw-body" onSubmit={(event) => void save(event)}>
        {error && <div className="contest-settings-error" role="alert">{error}</div>}
        {!config ? (
          error ? <button type="button" className="set-btn set-btn--secondary" onClick={() => void load()}>Retry loading settings</button> : <span className="set-hint">Loading settings…</span>
        ) : (
          <>
            <div className="set-field">
              <label className="set-label" htmlFor="contest-contact-emails">Contact emails</label>
              <textarea id="contest-contact-emails" className="set-input set-input-mono contest-settings-textarea" value={emails}
                onChange={(event) => setEmails(event.target.value)} disabled={saving} required spellCheck={false}
                aria-describedby="contest-contact-hint" />
              <p className="set-hint" id="contest-contact-hint">One email per line. These contacts are assigned across new cases. Add additional addresses here.</p>
            </div>
            <div className="set-field">
              <label className="set-label" htmlFor="contest-captcha-key">2Captcha API key</label>
              <input id="contest-captcha-key" type="password" className="set-input set-input-mono" value={key}
                onChange={(event) => setKey(event.target.value)} autoComplete="new-password" disabled={saving}
                placeholder={config.captcha_configured ? 'Leave blank to keep the current key' : 'Enter your 2Captcha API key'}
                aria-describedby="contest-captcha-hint" />
              <p className="set-hint" id="contest-captcha-hint">{config.captcha_configured ? 'A key is configured.' : 'A key is required before the team can start runs.'} The saved key is never displayed.</p>
            </div>
            <div className="set-team-pw-actions">
              <button type="submit" className="set-btn set-btn--primary" disabled={saving || !dirty || !emails.trim()}>{saving ? 'Saving…' : 'Save contest settings'}</button>
            </div>
          </>
        )}
      </form>
    </section>
  );
}
